import { FetchTransport, TransportItem } from '@grafana/faro-react';
import { IDBPDatabase, openDB } from 'idb';
import { TelemetryEvent, trackEvent } from './telemetry';

/**
 * Faro's FetchTransport drops a payload whenever the request fails — it logs and moves on, with
 * no retry and no persistence. For a field-first PWA that is exactly backwards: the signals we
 * most need are produced while the device is offline (`connectivity_changed` on losing signal,
 * `offline_command_enqueued`, and above all `offline_storage_write_failed`, which means the
 * technician's work was lost). All of those were being discarded.
 *
 * This transport buffers those payloads in IndexedDB and replays them once the device is back
 * online. Each signal already carries the timestamp of the moment it was produced, so replayed
 * data lands in Grafana at the right point in time rather than at the reconnect.
 *
 * Buffered signals are identifiable in Grafana without any extra marker: `beforeSend` stamps
 * every signal with `online`, so anything replayed carries `online: "false"` in its context.
 */

const DB_NAME = 'faro_offline_db';
const DB_VERSION = 1;
const STORE = 'batches';

// The queue shares its quota with the technician's photos and pending commands. Telemetry must
// never be the reason a work order cannot be saved, so the caps are deliberately modest and the
// oldest entries are dropped first.
const MAX_BATCHES = 200;
const MAX_BYTES = 2 * 1024 * 1024;

// Anything older than this is dropped instead of replayed, because Loki refuses samples beyond
// its ingestion window (`reject_old_samples_max_age`) and uploading them would just be waste.
//
// 24h is a deliberate assumption, not a measured value: the limit is a collector-side tenant
// setting we cannot read. It covers an overnight shift, which is the case this buffer exists for.
// See README ("Offline buffering") for how to measure the real window if it ever matters.
const MAX_REPLAY_AGE_MS = 24 * 60 * 60 * 1000;

interface BufferedBatch {
	id?: number;
	enqueuedAt: number;
	bytes: number;
	/** JSON-serialised TransportItem[] — plain data by the time beforeSend has run. */
	body: string;
}

// `name` is intentionally not overridden: FetchTransport declares it as a literal type, so a
// subclass cannot narrow it to a different string. It is only used for Faro's internal debug
// logging, and reporting as the fetch transport is accurate anyway.
export default class OfflineBufferedFetchTransport extends FetchTransport {
	private dbPromise?: Promise<IDBPDatabase | null>;

	private draining = false;

	private droppedWhileOffline = 0;

	/**
	 * Sends when online, buffers when not. Deliberately checks `navigator.onLine` rather than
	 * reacting to a failed request: FetchTransport swallows its own errors, so a failure is not
	 * observable from the outside. The trade-off is that an "online but the request failed" case
	 * is still lost — rare next to the offline case this exists for.
	 */
	async send(items: TransportItem[]): Promise<void> {
		if (navigator.onLine) {
			await super.send(items);
			return;
		}

		try {
			await this.persist(items);
		} catch {
			// Never let telemetry surface an error into the app.
			this.droppedWhileOffline += items.length;
		}
	}

	/** Replays everything buffered. Safe to call at any time; concurrent calls are ignored. */
	async drain(): Promise<void> {
		if (this.draining || !navigator.onLine) {
			return;
		}
		this.draining = true;

		try {
			const db = await this.openBuffer();
			if (!db) {
				return;
			}

			const batches: BufferedBatch[] = await db.getAll(STORE);
			const now = Date.now();
			let expired = 0;

			for (const batch of batches) {
				if (!navigator.onLine) {
					break;
				}

				const tooOld = now - batch.enqueuedAt > MAX_REPLAY_AGE_MS;
				if (!tooOld) {
					try {
						// Awaited so the replay is paced rather than firing every stored batch at once.
						// eslint-disable-next-line no-await-in-loop
						await super.send(JSON.parse(batch.body) as TransportItem[]);
					} catch {
						// A corrupt entry must not block the rest of the queue.
					}
				} else {
					expired += 1;
				}

				if (batch.id !== undefined) {
					// Deleted regardless of outcome: FetchTransport gives no delivery signal, so
					// retrying forever would grow the queue without bound.
					// eslint-disable-next-line no-await-in-loop
					await db.delete(STORE, batch.id);
				}
			}

			this.reportBufferHealth(batches.length - expired, expired);
		} catch {
			// Ignore — a failed drain is retried on the next online event.
		} finally {
			this.draining = false;
		}
	}

	/** Starts replaying on reconnect. Called once from initObservability. */
	listen(): void {
		window.addEventListener('online', () => {
			this.drain();
		});
	}

	private async persist(items: TransportItem[]): Promise<void> {
		const db = await this.openBuffer();
		if (!db) {
			return;
		}

		const body = JSON.stringify(items);
		const record: BufferedBatch = { enqueuedAt: Date.now(), bytes: body.length, body };

		await db.add(STORE, record);
		await this.enforceCaps(db);
	}

	/** Drops the oldest batches until the queue is back inside both caps. */
	private async enforceCaps(db: IDBPDatabase): Promise<void> {
		const batches: BufferedBatch[] = await db.getAll(STORE);
		let totalBytes = batches.reduce((sum, b) => sum + b.bytes, 0);
		let count = batches.length;

		for (const batch of batches) {
			if (count <= MAX_BATCHES && totalBytes <= MAX_BYTES) {
				break;
			}
			if (batch.id !== undefined) {
				// eslint-disable-next-line no-await-in-loop
				await db.delete(STORE, batch.id);
				this.droppedWhileOffline += 1;
				totalBytes -= batch.bytes;
				count -= 1;
			}
		}
	}

	private openBuffer(): Promise<IDBPDatabase | null> {
		if (!this.dbPromise) {
			this.dbPromise = openDB(DB_NAME, DB_VERSION, {
				upgrade(db) {
					if (!db.objectStoreNames.contains(STORE)) {
						db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
					}
				},
			}).catch(() => null);
		}
		return this.dbPromise;
	}

	/**
	 * Reports what the buffer did, so the replayed data can be trusted. Without this, a gap in the
	 * telemetry is indistinguishable from a period where genuinely nothing happened. Reported via
	 * the telemetry facade rather than `this.api` — transports, unlike instrumentations, are not
	 * given an API handle (BaseExtension has no `api`), so that would silently do nothing.
	 */
	private reportBufferHealth(attempted: number, expired: number): void {
		const dropped = this.droppedWhileOffline;
		if (attempted === 0 && expired === 0 && dropped === 0) {
			return;
		}
		this.droppedWhileOffline = 0;

		trackEvent(TelemetryEvent.TelemetryBufferDrained, {
			// "attempted", not "replayed": FetchTransport swallows the result of its own request,
			// so a batch the collector refuses (e.g. for being older than its ingestion window)
			// is indistinguishable here from one it accepted. Reporting these as replayed would
			// claim a delivery we cannot actually observe.
			attemptedBatches: attempted,
			expiredBatches: expired,
			droppedBatches: dropped,
		});
	}
}
